import { RequestGateMovementMutation } from '@/__generated__/graphql';
import { useModalStatusMessage } from '@/hooks';
import { useFormatters } from '@/hooks/useFormatters';
import { useMutation } from '@apollo/client';
import {
  classifyGateCommandOutcome,
  composeGateCommandMessage,
  GateCommandOutcome,
  severityForOutcome,
} from '@features/gateCommon/utils/gateCommandOutcome';
import { t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { useCallback } from 'react';
import { REQUEST_GATE_MOVEMENT } from '../graphql/mutations/requestGateMovement';

type RequestGateMovementPayload = RequestGateMovementMutation['requestGateMovement'];
type QueuedGateRequest = NonNullable<RequestGateMovementPayload['queuedRequest']>;

export interface MoveGateResult {
  outcome: GateCommandOutcome;
  code: string;
  /**
   * Viene en la aceptación y en `QUEUED` (la fila de la solicitud encolada). Un no-op, un rechazo,
   * `ALREADY_QUEUED` o un error de transporte llegan sin él.
   */
  movement: RequestGateMovementPayload['movement'];
}

interface UseMoveGateInput {
  /**
   * Se llama en **todos** los desenlaces, incluido el fallo: quien consume decide qué hacer con
   * cada uno. La notificación al operario ya la emite este hook; el callback es para la navegación.
   */
  onResult?: (result: MoveGateResult) => void;
}

export const useMoveGate = (input: UseMoveGateInput) => {
  const { i18n } = useLingui();
  const { openModalSuccessMessage, openModalWarningMessage, openModalErrorMessage } =
    useModalStatusMessage();
  const [mutate, { loading }] = useMutation(REQUEST_GATE_MOVEMENT);
  const { formatDate, formatNumber } = useFormatters();

  /**
   * `ALREADY_QUEUED` trae la solicitud pendiente: destino, cuándo se pidió y cuándo vence. Es lo
   * que el operario necesita para decidir si espera o la cancela. Cada dato que falte se omite.
   */
  const describeQueuedRequest = useCallback(
    (queued: QueuedGateRequest): string | null => {
      const withTime = { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' } as const;
      const parts = [
        queued.requestedPosition != null
          ? t(i18n)`destino ${formatNumber(queued.requestedPosition)}`
          : null,
        queued.requestedAt != null
          ? t(i18n)`pedida el ${formatDate(queued.requestedAt, withTime)}`
          : null,
        queued.expiresAt != null ? t(i18n)`vence el ${formatDate(queued.expiresAt, withTime)}` : null,
      ].filter((part): part is string => part != null);
      if (parts.length === 0) return null;
      return t(i18n)`Solicitud pendiente: ${parts.join(' · ')}.`;
    },
    [i18n, formatDate, formatNumber],
  );

  const notify = useCallback(
    (outcome: GateCommandOutcome, payload: { code: string; message?: string | null }) => {
      const message = composeGateCommandMessage(payload, outcome, i18n, 'move');
      const severity = severityForOutcome(outcome);
      if (severity === 'success') openModalSuccessMessage(message);
      else if (severity === 'warning') openModalWarningMessage(message);
      else openModalErrorMessage(message);
    },
    [i18n, openModalSuccessMessage, openModalWarningMessage, openModalErrorMessage],
  );

  const moveGate = useCallback(
    (gateId: string, position: number) => {
      mutate({
        variables: {
          input: {
            gateId,
            position,
          },
        },
        onCompleted({ requestGateMovement }) {
          const outcome = classifyGateCommandOutcome(requestGateMovement);
          const pending = requestGateMovement.queuedRequest
            ? describeQueuedRequest(requestGateMovement.queuedRequest)
            : null;
          notify(outcome, {
            code: requestGateMovement.code,
            message: [requestGateMovement.message, pending].filter(Boolean).join(' ') || null,
          });
          input.onResult?.({
            outcome,
            code: requestGateMovement.code,
            movement: requestGateMovement.movement,
          });
        },
        onError(error) {
          // Un error de red o una excepción del backend **no** son una negativa de la compuerta: el
          // comando pudo haberse enviado igual. Se trata como `unconfirmed` para no afirmar que no
          // hubo movimiento físico.
          console.error(error.message);
          notify('unconfirmed', {
            code: 'REQUEST_FAILED',
            message: t(i18n)`No se recibió respuesta al comando de movimiento.`,
          });
          input.onResult?.({ outcome: 'unconfirmed', code: 'REQUEST_FAILED', movement: null });
        },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mutate, i18n, notify, describeQueuedRequest],
  );

  return { moveGate, loading };
};
